import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger, ValidationError } from "@/lib/logger";
import { money } from "@/lib/format";
import type { Tables } from "@/types";
import { PageHeader } from "@/components/shared/PageHeader";
import { DeleteDialog } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContractTemplatesTab } from "@/modules/contracts/TemplatesTab";
import { UsersTab } from "./UsersTab";
import { CompanyTab } from "./CompanyTab";
import { TrashTab } from "@/modules/admin/TrashTab";
import { useAuth } from "@/app/auth";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type TaxRegime = Tables<"tax_regimes">;
type FeeType = Tables<"fee_types">;

export function useTaxRegimes(activeOnly = false) {
  return useQuery({
    queryKey: ["tax_regimes", activeOnly],
    queryFn: async () => {
      let q = supabase.from("tax_regimes").select("*").order("position").order("label");
      if (activeOnly) q = q.eq("is_active", true);
      const { data, error } = await q; if (error) throw error; return data;
    },
  });
}
export function useFeeTypes(activeOnly = false) {
  return useQuery({
    queryKey: ["fee_types", activeOnly],
    queryFn: async () => {
      let q = supabase.from("fee_types").select("*").order("position").order("label");
      if (activeOnly) q = q.eq("is_active", true);
      const { data, error } = await q; if (error) throw error; return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Régimes fiscaux
// ---------------------------------------------------------------------------
function TaxRegimeForm({ regime, onDone }: { regime?: TaxRegime; onDone: () => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState(regime?.label ?? "");
  const [rate, setRate] = useState(regime ? String(regime.rate_pct) : "0");
  const [mention, setMention] = useState(regime?.mention ?? "");
  const [isDefault, setIsDefault] = useState(regime?.is_default ?? false);
  const [active, setActive] = useState(regime?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const r = Number(rate);
      if (!label.trim()) throw new ValidationError("Le libellé est requis.");
      if (Number.isNaN(r) || r < 0 || r > 100) throw new ValidationError("Le taux doit être entre 0 et 100.");
      // Un seul régime par défaut : on retire le drapeau des autres avant de le poser.
      if (isDefault) { const { error } = await supabase.from("tax_regimes").update({ is_default: false }).eq("is_default", true); if (error) throw error; }
      const payload = { label: label.trim(), rate_pct: r, mention: mention.trim() || null, is_default: isDefault, is_active: active };
      const { error } = regime ? await supabase.from("tax_regimes").update(payload).eq("id", regime.id) : await supabase.from("tax_regimes").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["tax_regimes"] }); toast.success("Régime fiscal enregistré"); onDone(); },
    onError: (e) => { setError(e instanceof Error ? e.message : "Enregistrement impossible."); if (!(e instanceof ValidationError)) logger.error("tax_regime.save", e); },
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
      <div className="space-y-1.5"><Label htmlFor="tr-label">Libellé *</Label><Input id="tr-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="TVA 20 %" /></div>
      <div className="space-y-1.5"><Label htmlFor="tr-rate">Taux (%) *</Label><Input id="tr-rate" type="number" step="0.01" min="0" max="100" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
      <div className="space-y-1.5"><Label htmlFor="tr-mention">Mention imprimée sur les documents</Label><Input id="tr-mention" value={mention} onChange={(e) => setMention(e.target.value)} placeholder="Ex. Autoliquidation — art. 196 directive 2006/112/CE" /></div>
      <div className="flex flex-wrap gap-6 text-sm">
        <label className="flex items-center gap-2"><Switch checked={isDefault} onCheckedChange={setIsDefault} /> Régime par défaut des nouveaux clients</label>
        <label className="flex items-center gap-2"><Switch checked={active} onCheckedChange={setActive} /> Actif</label>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>{regime ? "Enregistrer" : "Créer le régime"}</Button></div>
    </form>
  );
}

function TaxRegimesTab() {
  const { data } = useTaxRegimes();
  const [editing, setEditing] = useState<TaxRegime | "new" | null>(null);
  const [deleting, setDeleting] = useState<TaxRegime | null>(null);
  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">Chaque client a un régime fiscal. Le taux et la mention sont copiés sur chaque devis, commande et facture au moment de leur création. <strong>Aucune exonération n'est présumée</strong> : ajuste cette liste avec ton expert-comptable.</p>
        <Button size="sm" onClick={() => setEditing("new")}><Plus className="mr-2 h-4 w-4" />Nouveau régime</Button>
      </div>
      <div className="glass overflow-hidden rounded-2xl">
        <Table>
          <TableHeader><TableRow><TableHead>Libellé</TableHead><TableHead className="text-right">Taux</TableHead><TableHead>Mention</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {data?.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => setEditing(r)}>
                <TableCell className="font-medium">{r.label} {r.is_default && <Badge className="ml-2">par défaut</Badge>}</TableCell>
                <TableCell className="text-right">{Number(r.rate_pct)} %</TableCell>
                <TableCell className="max-w-md truncate text-sm text-muted-foreground">{r.mention ?? "—"}</TableCell>
                <TableCell><Badge variant={r.is_active ? "secondary" : "outline"}>{r.is_active ? "Actif" : "Inactif"}</Badge></TableCell>
                <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label={`Supprimer ${r.label}`} onClick={(e) => { e.stopPropagation(); setDeleting(r); }}><Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing === "new" ? "Nouveau régime fiscal" : "Modifier le régime fiscal"}</DialogTitle><DialogDescription>Le changement ne touche pas les documents déjà créés.</DialogDescription></DialogHeader>
          {editing !== null && <TaxRegimeForm regime={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
      {deleting && <DeleteDialog entity="tax_regime" id={deleting.id} open onOpenChange={(o) => !o && setDeleting(null)} onDeleted={() => setDeleting(null)} onArchived={() => setDeleting(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types de frais
// ---------------------------------------------------------------------------
function FeeTypeForm({ fee, onDone }: { fee?: FeeType; onDone: () => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState(fee?.label ?? "");
  const [amount, setAmount] = useState(fee?.default_amount === null || fee?.default_amount === undefined ? "" : String(fee.default_amount));
  const [description, setDescription] = useState(fee?.description ?? "");
  const [active, setActive] = useState(fee?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      if (!label.trim()) throw new ValidationError("Le libellé est requis.");
      const a = amount === "" ? null : Number(amount);
      if (a !== null && (Number.isNaN(a) || a < 0)) throw new ValidationError("Le montant doit être positif.");
      const payload = { label: label.trim(), default_amount: a, description: description.trim() || null, is_active: active };
      const { error } = fee ? await supabase.from("fee_types").update(payload).eq("id", fee.id) : await supabase.from("fee_types").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["fee_types"] }); toast.success("Type de frais enregistré"); onDone(); },
    onError: (e) => { setError(e instanceof Error ? e.message : "Enregistrement impossible."); if (!(e instanceof ValidationError)) logger.error("fee_type.save", e); },
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
      <div className="space-y-1.5"><Label htmlFor="ft-label">Libellé *</Label><Input id="ft-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Frais de mise en place" /></div>
      <div className="space-y-1.5"><Label htmlFor="ft-amount">Montant par défaut (dans la devise du document)</Label><Input id="ft-amount" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Laisser vide pour saisir à chaque fois" /></div>
      <div className="space-y-1.5"><Label htmlFor="ft-desc">Description</Label><Input id="ft-desc" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={active} onCheckedChange={setActive} /> Actif</label>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>{fee ? "Enregistrer" : "Créer le type de frais"}</Button></div>
    </form>
  );
}

function FeeTypesTab() {
  const { data } = useFeeTypes();
  const [editing, setEditing] = useState<FeeType | "new" | null>(null);
  const [deleting, setDeleting] = useState<FeeType | null>(null);
  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">Les frais que tu peux ajouter à un devis ou une commande. <strong>Aucun frais n'est ajouté automatiquement</strong> : tu les choisis document par document.</p>
        <Button size="sm" onClick={() => setEditing("new")}><Plus className="mr-2 h-4 w-4" />Nouveau type de frais</Button>
      </div>
      {data && data.length === 0 ? (
        <div className="glass rounded-2xl border-dashed px-6 py-10 text-center text-sm text-muted-foreground">Aucun type de frais. Crée-en un, ou ajoute des frais libres directement sur un devis.</div>
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader><TableRow><TableHead>Libellé</TableHead><TableHead className="text-right">Montant par défaut</TableHead><TableHead>Description</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data?.map((f) => (
                <TableRow key={f.id} className="cursor-pointer" onClick={() => setEditing(f)}>
                  <TableCell className="font-medium">{f.label}</TableCell>
                  <TableCell className="text-right">{f.default_amount === null ? "à saisir" : money(f.default_amount)}</TableCell>
                  <TableCell className="max-w-md truncate text-sm text-muted-foreground">{f.description ?? "—"}</TableCell>
                  <TableCell><Badge variant={f.is_active ? "secondary" : "outline"}>{f.is_active ? "Actif" : "Inactif"}</Badge></TableCell>
                  <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label={`Supprimer ${f.label}`} onClick={(e) => { e.stopPropagation(); setDeleting(f); }}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing === "new" ? "Nouveau type de frais" : "Modifier le type de frais"}</DialogTitle></DialogHeader>
          {editing !== null && <FeeTypeForm fee={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
      {deleting && <DeleteDialog entity="fee_type" id={deleting.id} open onOpenChange={(o) => !o && setDeleting(null)} onDeleted={() => setDeleting(null)} onArchived={() => setDeleting(null)} />}
    </div>
  );
}

export default function SettingsPage() {
  const { roles } = useAuth();
  const isAdmin = roles.includes("admin");
  return (
    <div>
      <PageHeader title="Paramètres" description="Société, équipe, régimes fiscaux, types de frais, modèles de contrat et corbeille." />
      <Tabs defaultValue="company">
        <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="company">Société</TabsTrigger>
          {isAdmin && <TabsTrigger value="users">Utilisateurs</TabsTrigger>}
          <TabsTrigger value="tax">Régimes fiscaux</TabsTrigger>
          <TabsTrigger value="fees">Types de frais</TabsTrigger>
          <TabsTrigger value="contracts">Modèles de contrat</TabsTrigger>
          <TabsTrigger value="trash">Corbeille</TabsTrigger>
        </TabsList>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        {isAdmin && <TabsContent value="users"><UsersTab /></TabsContent>}
        <TabsContent value="tax"><TaxRegimesTab /></TabsContent>
        <TabsContent value="fees"><FeeTypesTab /></TabsContent>
        <TabsContent value="contracts"><ContractTemplatesTab /></TabsContent>
        <TabsContent value="trash"><TrashTab /></TabsContent>
      </Tabs>
    </div>
  );
}
